import { useCallback, useEffect, useRef, useState } from 'react'
import { apiKeysAdminApi } from '../../../services/admin/apiKeysAdminApi'
import type { EligibleAccount } from '../../../types/apiKeyAdmin'

/**
 * Account picker for admin key issuance.
 *
 * A free-text email box put the burden of remembering an exact address on the
 * admin, and a typo only surfaced as a 404 after submitting. This lists real
 * accounts up front and narrows as you type.
 *
 * Filtering is server-side rather than fetch-everything-and-filter-locally:
 * the account list grows without bound, and the endpoint already excludes
 * accounts that cannot own a key, so anything selectable here will be accepted.
 *
 * Free text is still allowed — an admin who knows the address shouldn't be
 * blocked by a list that hasn't loaded yet.
 */
export default function AccountPicker({
  value,
  onChange,
  autoFocus,
}: {
  value: string
  onChange: (email: string) => void
  autoFocus?: boolean
}) {
  const [accounts, setAccounts] = useState<EligibleAccount[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const search = useCallback(async (q: string) => {
    setLoading(true)
    try {
      const result = await apiKeysAdminApi.eligibleAccounts(q)
      setAccounts(result.accounts)
      setHighlighted(0)
    } catch {
      // A failed lookup must not block issuance — the field still accepts a
      // typed address, and the server validates it either way.
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Debounced so a fast typist doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => search(value), value ? 250 : 0)
    return () => clearTimeout(timer)
  }, [value, search])

  // Close when focus leaves the whole control, not just the input — clicking an
  // option would otherwise blur the input and unmount the list mid-click.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [])

  const select = (account: EligibleAccount) => {
    onChange(account.email)
    setOpen(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setOpen(true)
      return
    }
    if (!open || accounts.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlighted((i) => (i + 1) % accounts.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlighted((i) => (i - 1 + accounts.length) % accounts.length)
    } else if (event.key === 'Enter') {
      // Only claim Enter when a suggestion is actually highlighted, so it can
      // still submit the form when the admin typed a full address.
      event.preventDefault()
      select(accounts[highlighted])
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Search by email or name"
        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
        autoFocus={autoFocus}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls="account-picker-list"
      />

      {open && (
        <ul
          id="account-picker-list"
          role="listbox"
          className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-lg"
        >
          {loading && (
            <li className="px-3 py-2 text-sm text-gray-500">Searching…</li>
          )}

          {!loading && accounts.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">
              {value
                ? 'No matching account that can hold a key'
                : 'No accounts found'}
            </li>
          )}

          {!loading &&
            accounts.map((account, index) => (
              <li key={account.user_id} role="option" aria-selected={index === highlighted}>
                <button
                  type="button"
                  onClick={() => select(account)}
                  onMouseEnter={() => setHighlighted(index)}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm ${
                    index === highlighted ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-gray-900">{account.email}</span>
                    {account.name && (
                      <span className="block truncate text-xs text-gray-500">{account.name}</span>
                    )}
                  </span>
                  <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {account.role}
                  </span>
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  )
}
